import axios from 'src/utils/axios';
import helpers from 'src/helpers';
import { NOTIFICATION_STATUS } from 'src/constants';
import { addNotification } from 'src/actions/notificationActions';

// Action Types
export const GET_VALUE_LISTS = '@value-lists/get-value-lists';
export const GET_VALUE_LISTS_SUCCESS = '@value-lists/get-value-lists-success';
export const GET_VALUE_LISTS_ERROR = '@value-lists/get-value-lists-error';
export const CLEAR_VALUE_LISTS = '@value-lists/clear-value-lists';

export const CREATE_VALUE_LIST_ERROR = '@value-lists/create-value-list-error';
export const CREATE_VALUE_LIST_SUCCESS = '@value-lists/create-value-list-success';
export const UPDATE_VALUE_LIST_ERROR = '@value-lists/update-value-list-error';
export const UPDATE_VALUE_LIST_SUCCESS = '@value-lists/update-value-list-success';
export const DELETE_VALUE_LIST_ERROR = '@value-lists/delete-value-list-error';
export const DELETE_VALUE_LIST_SUCCESS = '@value-lists/delete-value-list-success';
export const COPY_VALUE_LIST_ERROR = '@value-lists/copy-value-list-error';
export const COPY_VALUE_LIST_SUCCESS = '@value-lists/copy-value-list-success';
export const REORDER_VALUE_LISTS_ERROR = '@value-lists/reorder-value-lists-error';
export const SET_BACK_TO_VALUE_LISTS = '@value-lists/set-back-to-list';

// Action Creators

export const clearValueLists = () => ({
  type: CLEAR_VALUE_LISTS,
});

// Back-navigation state (search/filters/scroll restore when returning from
// the detail page). Pass false to reset entirely.
export const setBackToValueLists = (payload) => ({
  type: SET_BACK_TO_VALUE_LISTS,
  payload,
});

const extractErrorMessage = (e, fallback) => {
  const { error, message } = e?.response?.data || {};
  return helpers.transform.toString(message || error || fallback);
};

export const getValueLists = (listType = 'smartFieldOptions') => async (dispatch) => {
  dispatch({
    type: GET_VALUE_LISTS,
  });
  try {
    const response = await axios.get('/api/valueLists', { params: { listType } });
    dispatch({
      type: GET_VALUE_LISTS_SUCCESS,
      payload: {
        lists: response?.data?.lists || [],
        companyName: response?.data?.companyName || null,
        listType,
      },
    });
  } catch (e) {
    dispatch(addNotification(
      GET_VALUE_LISTS_ERROR,
      extractErrorMessage(e, 'Server error'),
      NOTIFICATION_STATUS.ERROR
    ));
    dispatch({
      type: GET_VALUE_LISTS_ERROR,
    });
  }
};

// The write thunks return the affected list (or true) on success and null on
// failure, so callers can await them and refresh/navigate on success.

export const createValueList = (payload) => async (dispatch) => {
  try {
    const response = await axios.post('/api/valueLists', payload);
    dispatch(addNotification(
      CREATE_VALUE_LIST_SUCCESS,
      'List created',
      NOTIFICATION_STATUS.SUCCESS
    ));
    return response?.data?.list || null;
  } catch (e) {
    dispatch(addNotification(
      CREATE_VALUE_LIST_ERROR,
      extractErrorMessage(e, 'Unable to create list'),
      NOTIFICATION_STATUS.ERROR
    ));
    return null;
  }
};

export const updateValueList = (listId, payload) => async (dispatch) => {
  try {
    const response = await axios.put(`/api/valueLists/${listId}`, payload);
    dispatch(addNotification(
      UPDATE_VALUE_LIST_SUCCESS,
      'List updated',
      NOTIFICATION_STATUS.SUCCESS
    ));
    return response?.data?.list || null;
  } catch (e) {
    dispatch(addNotification(
      UPDATE_VALUE_LIST_ERROR,
      extractErrorMessage(e, 'Unable to update list'),
      NOTIFICATION_STATUS.ERROR
    ));
    return null;
  }
};

export const deleteValueList = (listId) => async (dispatch) => {
  try {
    await axios.delete(`/api/valueLists/${listId}`);
    dispatch(addNotification(
      DELETE_VALUE_LIST_SUCCESS,
      'List deleted',
      NOTIFICATION_STATUS.SUCCESS
    ));
    return true;
  } catch (e) {
    dispatch(addNotification(
      DELETE_VALUE_LIST_ERROR,
      extractErrorMessage(e, 'Unable to delete list'),
      NOTIFICATION_STATUS.ERROR
    ));
    return null;
  }
};

export const copyValueList = (listId) => async (dispatch) => {
  try {
    const response = await axios.post(`/api/valueLists/${listId}/copy`);
    const copied = response?.data?.list || null;
    dispatch(addNotification(
      COPY_VALUE_LIST_SUCCESS,
      copied ? `Copied to My Lists as "${copied.name}"` : 'Copied to My Lists',
      NOTIFICATION_STATUS.SUCCESS
    ));
    return copied;
  } catch (e) {
    dispatch(addNotification(
      COPY_VALUE_LIST_ERROR,
      extractErrorMessage(e, 'Unable to copy list'),
      NOTIFICATION_STATUS.ERROR
    ));
    return null;
  }
};

export const reorderValueLists = (updates) => async (dispatch) => {
  try {
    await axios.put('/api/valueLists/reorder', { updates });
    return true;
  } catch (e) {
    dispatch(addNotification(
      REORDER_VALUE_LISTS_ERROR,
      extractErrorMessage(e, 'Unable to reorder lists'),
      NOTIFICATION_STATUS.ERROR
    ));
    return null;
  }
};
